import React, { useState } from 'react';
import { SignedText } from '../../../components/ui/SignedValue';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Icon, { type IconName } from '../../../components/AppIcon';
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';

export type KPIMetric = {
  id: string;
  title: string;
  value: string;
  /** Only present when a real prior-period comparison is available */
  change?: string;
  /** Only meaningful when `change` is present */
  trend?: 'up' | 'down';
  /** When true, render the trend chip in a neutral tone (e.g. "+5 net") */
  isNeutralChange?: boolean;
  /** When true, `value` is a state ("Not run yet"), not a figure, and renders muted. */
  isPlaceholderValue?: boolean;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'red';
  category: 'attendance' | 'payroll' | 'employee' | 'project' | 'engagement';
  description: string;
  /** CO-610 — per-currency remainders that could not be folded into `value` ("+ 12,000 AED"). */
  supplement?: string[];
  /** CO-610 — the reason for `supplement` and where to fix it. */
  notice?: { text: string; href: string; linkLabel: string };
};

export type KPICardProps = {
  metric: KPIMetric;
  isPrimary?: boolean;
};

const KPICard: React.FC<KPICardProps> = ({ metric, isPrimary = false }) => {
  const { t } = useTranslation();
  const [showDescription, setShowDescription] = useState(false);

  const getIconStyles = (color: KPIMetric['color']) => {
    switch (color) {
      case 'blue':
        return 'p-2 bg-primary/10 rounded-lg text-primary';
      case 'green':
        return 'p-2 bg-success/10 rounded-lg text-success';
      case 'orange':
        return 'p-2 bg-warning/10 rounded-lg text-warning';
      case 'purple':
        return 'p-2 bg-purple-100 rounded-lg text-purple-600';
      case 'red':
        return 'p-2 bg-error/10 rounded-lg text-error';
      default:
        return 'p-2 bg-muted rounded-lg text-muted-foreground';
    }
  };

  const hasRealChange = metric?.change != null;

  const renderTrendChip = () => {
    // No comparison available: render nothing. "No comparison" repeated on most tiles was noise.
    if (!hasRealChange) return null;
    if (metric?.isNeutralChange) {
      return (
        <span className="inline-flex items-center text-xs font-medium text-muted-foreground tabular-nums">
          <SignedText text={metric.change ?? ''} />
        </span>
      );
    }
    // `trend` is the GOODNESS of the change (Late % falling is 'up' = good), so it picks the
    // colour only. The arrow follows the SIGN of the change itself, so "-53.8%" never points up.
    const isGood = metric?.trend === 'up';
    const changeText = (metric.change ?? '').trim();
    const pointsDown = /^[-−]/.test(changeText);
    const pointsUp = /^\+/.test(changeText);
    const arrowDown = pointsDown || (!pointsUp && !isGood);
    return (
      <span className={cn(
        'inline-flex items-center text-xs font-medium tabular-nums',
        isGood ? 'text-success' : 'text-error',
      )}>
        {arrowDown ? <TrendingDown className="w-3 h-3 me-1" /> : <TrendingUp className="w-3 h-3 me-1" />}
        <SignedText text={metric.change ?? ''} />
      </span>
    );
  };

  return (
    <Card
      variant="elevated"
      className={cn(
        'relative hover:shadow-md transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        isPrimary ? 'p-6' : 'p-4',
      )}
      tabIndex={0}
      onMouseEnter={() => setShowDescription(true)}
      onMouseLeave={() => setShowDescription(false)}
      onFocus={() => setShowDescription(true)}
      onBlur={() => setShowDescription(false)}
      data-testid={`kpi-card-${metric?.id || 'unknown'}`}
      data-kpi-category={metric?.category}
      data-kpi-primary={isPrimary}
    >
      <div className="flex items-start gap-3 relative z-10">
        <div className={getIconStyles(metric?.color)}>
          <Icon name={metric?.icon as IconName} size={isPrimary ? 24 : 20} />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className={cn(
              'font-medium text-muted-foreground mb-1 truncate',
              isPrimary ? 'text-sm' : 'text-xs',
            )}
            data-testid="kpi-title"
          >
            {metric?.title}
          </h4>
          <p
            className={cn(
              metric?.isPlaceholderValue
                ? 'font-medium text-muted-foreground'
                : 'font-bold text-foreground tabular-nums',
              metric?.isPlaceholderValue
                ? (isPrimary ? 'text-lg' : 'text-base')
                : (isPrimary ? 'text-2xl' : 'text-xl'),
            )}
            data-testid="kpi-value"
          >
            {metric ? <SignedText text={metric.value} /> : null}
          </p>
          {/* CO-610 — amounts with no FX rate stay in their own currency, one line each, under the
              converted figure. They are deliberately NOT part of `kpi-value`: the headline is only
              ever money in one currency. */}
          {metric?.supplement && metric.supplement.length > 0 && (
            <ul className="mt-0.5 space-y-0.5" data-testid="kpi-unconverted">
              {metric.supplement.map((line) => (
                <li
                  key={line}
                  className={cn('font-semibold text-foreground/80 tabular-nums', isPrimary ? 'text-base' : 'text-sm')}
                >
                  {line}
                </li>
              ))}
            </ul>
          )}
          {metric?.notice && (
            <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-amber-800 dark:text-amber-200" data-testid="kpi-notice">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {metric.notice.text}
                {' '}
                <Link
                  to={metric.notice.href}
                  className="font-medium underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                  data-testid="kpi-notice-link"
                >
                  {metric.notice.linkLabel}
                </Link>
              </span>
            </p>
          )}
          {hasRealChange && (
            <div className="flex items-center justify-between mt-2 gap-2">
              {renderTrendChip()}
              {!metric?.isNeutralChange && (
                <span className="text-[11px] text-muted-foreground truncate">{t('vs_previous')}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hover description tooltip */}
      {showDescription && (
        <div className="absolute inset-x-0 top-full mt-2 z-20 bg-foreground text-background text-xs rounded-lg p-3 shadow-xl max-w-sm pointer-events-none">
          <div className="absolute -top-1 start-4 w-2 h-2 bg-foreground rotate-45"></div>
          {metric?.description}
        </div>
      )}
    </Card>
  );
};

export default KPICard;
